import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Mail, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationPreferences = () => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/notifications/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(response.data);
    } catch (error) {
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    setSaving(true);

    try {
      await axios.put(`${API}/notifications/preferences`, preferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification preferences updated successfully');
    } catch (error) {
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const notificationTypes = [
    { key: 'reservation_accepted', label: 'Reservation Accepted', description: 'When a resource accepts your reservation request (PM)' },
    { key: 'reservation_rejected', label: 'Reservation Rejected', description: 'When a resource rejects your reservation request (PM)' },
    { key: 'prereservation_request', label: 'Pre-Reservation Request', description: 'When a PM pre-reserves your resource (RM)' },
    { key: 'rm_interest_project', label: 'RM Shows Interest', description: 'When an RM shows interest in your project (PM)' },
    { key: 'pm_favorited_resource', label: 'Resource Favorited', description: 'When a PM adds your resource to favorites (RM)' },
    { key: 'new_message', label: 'New Messages', description: 'When you receive a new message' },
    { key: 'project_expiring', label: 'Project Expiring', description: 'Reminders about upcoming project deadlines (PM)' },
    { key: 'resource_available_soon', label: 'Resource Available Soon', description: 'When your resource becomes available soon (RM)' },
    { key: 'profile_updated', label: 'Profile Updates', description: 'Confirmations of profile changes' },
    { key: 'billing_changed', label: 'Billing Changes', description: 'Important billing and payment notifications' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#62D84E' }}></div>
      </div>
    );
  }

  if (!preferences) return null;

  return (
    <div className="space-y-6">
      {/* Global Settings */}
      <Card>
        <CardHeader>
          <CardTitle style={{ color: '#1B3240' }}>Global Notification Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">In-App Notifications</p>
                <p className="text-sm text-gray-600">Show notifications in the application</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.in_app_enabled}
                onChange={(e) => setPreferences({ ...preferences, in_app_enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-gray-900">Email Notifications</p>
                <p className="text-sm text-gray-600">Receive notifications via email</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.email_enabled}
                onChange={(e) => setPreferences({ ...preferences, email_enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Settings */}
      <Card>
        <CardHeader>
          <CardTitle style={{ color: '#1B3240' }}>Notification Types</CardTitle>
          <p className="text-sm text-gray-600 mt-2">Choose how you want to be notified for each event</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {notificationTypes.map((type) => (
              <div key={type.key} className="p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-gray-900">{type.label}</p>
                    <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                  </div>
                </div>
                
                <div className="flex gap-6 mt-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences[`inapp_${type.key}`] !== false}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        [`inapp_${type.key}`]: e.target.checked
                      })}
                      className="w-4 h-4 text-blue-600 rounded"
                      disabled={!preferences.in_app_enabled}
                    />
                    <Smartphone className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-gray-700">In-App</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences[`email_${type.key}`] !== false}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        [`email_${type.key}`]: e.target.checked
                      })}
                      className="w-4 h-4 text-green-600 rounded"
                      disabled={!preferences.email_enabled}
                    />
                    <Mail className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-700">Email</span>
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className="pt-6">
            <Button
              onClick={handleSave}
              className="btn-snow"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationPreferences;
